import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { env } from "@environments/environment";
import { DistributionPageDto } from "@app/ui_models/web-response";
import {
  DistributionDozQuantityRequest,
  DistributionQuantityRequest,
  DistributionSendToDozRequest,
} from "@app/ui_models/model-dto";

@Injectable({ providedIn: "root" })
export class DistributionController {

  public constructor(httpService: HttpClient) {
    this.httpService = httpService;
  }

  httpService: HttpClient;
  protected baseUrl = `${env.logisticsServiceBackend}/logistics/v1/distributions`;

  public createDistribution(distributionRequest: any): Observable<any>  {
    const headers = new HttpHeaders().set("Content-type", "application/json");
    return this.httpService.post("/logistics/v1/distributions/create", JSON.stringify(distributionRequest), { headers, responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionPreview(id: number): Observable<any>  {
    return this.httpService.get(`${this.baseUrl}/view/${id}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionPreviewDoz(id: number): Observable<any>  {
    return this.httpService.get(`${this.baseUrl}/doz/view/${id}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistribution(id: number): Observable<any>  {
    return this.httpService.get(`${this.baseUrl}/${id}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionDoz(id: number): Observable<DistributionPageDto>  {
    return this.httpService.get(`${this.baseUrl}/doz/${id}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionDozForRegion(id: number, regionId: number): Observable<DistributionPageDto>  {
    return this.httpService.get(`${this.baseUrl}/doz/${id}/${regionId}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionDozForRegions(id: number, regionIds: number[]): Observable<DistributionPageDto[]>  {
    const regIds = regionIds.join(",");
    return this.httpService.get(`${this.baseUrl}/doz/${id}/${regIds}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionsDoz(params: HttpParams): Observable<any>  {
    return this.httpService.get(`${this.baseUrl}/doz/registry`, { params, responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public getDistributionsMzu(params: HttpParams): Observable<any>  {
    return this.httpService.get(`${this.baseUrl}/registry-mzu`, { params, responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public updateCoefficient(distributionGoodId: number, coefficient:number): Observable<void> {
    return this.httpService.put<void>(`${this.baseUrl}/coefficient?distributionGoodId=${distributionGoodId}&coefficient=${coefficient}`, { responseType: "text" });
  }

  public regionActivation(regionRecipientId: number, active:boolean): Observable<void> {
    return this.httpService.put<void>(`${this.baseUrl}/region-activation?regionRecipientId=${regionRecipientId}&active=${active}`, { responseType: "text" });
  }

  public updateDistributionQuantity(quantities: DistributionQuantityRequest[]): Observable<any>  {
    const headers = new HttpHeaders().set("Content-type", "application/json");
    return this.httpService.put(`${this.baseUrl}/quantity`, JSON.stringify(quantities), { headers, responseType: "text" });
  }

  public approve(quantities: DistributionQuantityRequest[]): Observable<any>  {
    const headers = new HttpHeaders().set("Content-type", "application/json");
    return this.httpService.put(`${this.baseUrl}/approve`, JSON.stringify(quantities), { headers, responseType: "text" });
  }

  public distributeAll(distributionGoodId: number): Observable<void> {
    return this.httpService.put<void>(`${this.baseUrl}/distribute-all/${distributionGoodId}`, { responseType: "text" });
  }

  public removeDistributionGood(distributionGoodId: number): Observable<void> {
    return this.httpService.delete<void>(`${this.baseUrl}/remove-distribution-good/${distributionGoodId}`);
  }

  public checkDistributionGood(distributionGoodId: number, checked: boolean) {
    return this.httpService.patch<void>(`${this.baseUrl}/check-distribution-good/${distributionGoodId}?checked=${checked}`, { responseType: "text" });
  }

  public sendToDoz(distribution: DistributionSendToDozRequest): Observable<any>  {
    const headers = new HttpHeaders().set("Content-type", "application/json");
    return this.httpService.put(`${this.baseUrl}/send-to-doz`, JSON.stringify(distribution), { headers, responseType: "text" });
  }

  public backToDraft(distributionId: number) {
    return this.httpService.put<void>(`${this.baseUrl}/back-to-draft/${distributionId}`, { responseType: "text" });
  }

  public refusal(distributionId: number) {
    return this.httpService.put<void>(`${this.baseUrl}/doz/refusal/${distributionId}`, { responseType: "text" });
  }

  public updateDistribution100Financed(distributionGoodId: number, distributionId: number) {
    return this.httpService.put<void>(`${this.baseUrl}/update/Requisition100Financed/${distributionGoodId}/${distributionId}`, { responseType: "text" });
  }

  public updateRemnants(distributionGoodId: number, distributionId: number) {
    return this.httpService.put<void>(`${this.baseUrl}/update/remnants/${distributionGoodId}/${distributionId}`, { responseType: "text" });
  }

  public updateDistributedQuantity(request: DistributionDozQuantityRequest []): Observable<any> {
    const headers = new HttpHeaders().set("Content-type", "application/json");
    return this.httpService.put(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/quantity`, JSON.stringify(request),  { headers, responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public confirmDistribution(distributionId: number) {
    return this.httpService.post(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/confirm/${distributionId}`, { responseType: "text" });
  }

  public archive(distributionId: number) {
    return this.httpService.put<void>(`${this.baseUrl}/archive/${distributionId}`, { responseType: "text" });
  }

  public getHash(transactionDocumentId: number): Observable<any> {
    return this.httpService.get(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/signing/hash/${transactionDocumentId}`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public confirm(transactionDocumentId: number, signedHash: string) {
    return this.httpService.post(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/signing/confirm/receipt/${transactionDocumentId}`, signedHash, { responseType: "text" });
  }

  public cancel(transactionDocumentId: number) {
    return this.httpService.post(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/signing/cancel/receipt/${transactionDocumentId}`, { responseType: "text" });
  }

  public testHashForAccounting(transactionDocumentId: number): Observable<any> {
    return this.httpService.get(`${env.accounting}/test/document/receipt/${transactionDocumentId}/4hash`, { responseType: "text" }).pipe(map(res => (res ? JSON.parse(res) : undefined)));
  }

  public setDocumentType(transactionDocumentId: number, documentType: string) {
    return this.httpService.post(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/document/type/setup/${transactionDocumentId}`, documentType, { responseType: "text" });
  }

  public partiallyApprove(distributionId: number, regionId: number) {
    return this.httpService.put(`${env.logisticsServiceBackend}/logistics/v1/distributions/doz/approve/partially/${distributionId}/${regionId}`,  { responseType: "text" });
  }
}
